import {
  ArrowRightIcon,
  CopyIcon,
  KeyboardIcon,
  LogOutIcon,
  PanelLeftIcon,
  PlusIcon,
  type LucideIcon,
} from "lucide-react"
import { createContext, useContext, useRef, useState, type ReactNode } from "react"
import { useMatch, useNavigate } from "react-router"
import type { Project } from "@beacon/core/projects"
import {
  colourSchemes,
  copyText,
  IdentityTile,
  Kbd,
  useColourScheme,
} from "@/design-system/index.ts"
import {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "@/primitives/combobox.tsx"
import { Dialog, DialogContent, DialogTitle } from "@/primitives/dialog.tsx"
import { useSidebar } from "@/primitives/sidebar.tsx"
import type { ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { answers, ticketJumpOf } from "./describe.ts"
import { adminDestinations, destinations } from "./destinations.ts"

/*
 * One box to go anywhere or do anything from: ⌘K, `/` on a page with no search of its own, or the
 * sidebar's search. It offers the pages the Member can reach, their Projects, a ticket by its key,
 * and the shell's actions, and narrows them as they type.
 *
 * It is a Combobox list held open inside a Dialog at the top of the viewport, so the list grows
 * downwards without the input moving. The palette filters for itself, so it knows when nothing is
 * left and can pin the ticket a typed key names above everything else.
 */

type PaletteContext = { openPalette: () => void }

/** Opens the command palette, for a page's own "Search" button (Not found has one). */
export const CommandPaletteContext = createContext<PaletteContext>({
  openPalette: () => undefined,
})

export const useCommandPalette = () => useContext(CommandPaletteContext)

const groups = ["Jump to ticket", "Go to", "Projects", "Actions"] as const

type Entry = {
  id: string
  group: (typeof groups)[number]
  label: string
  /** Faint text after the label: a Project's key, "Current" beside the scheme in use. */
  secondary?: string
  glyph: ReactNode
  shortcut?: ShortcutId
  run: () => void
}

/**
 * The first row is highlighted from the moment the palette opens, so ⌘K then ↵ goes Home, and
 * after every keystroke. Base UI's Combobox does this (`"always"`), but its public type admits only
 * `true`, which waits for the first keystroke, so the value is passed through as the type it hides.
 */
const highlightFirst = "always" as unknown as boolean

const icon = (Icon: LucideIcon) => <Icon aria-hidden className="size-4 text-muted-foreground" />

/**
 * The palette itself, open while `open`. `projects` are the Member's own (the sidebar's list), the
 * rest is what the shell can do for them.
 */
export function CommandPalette({
  open,
  onOpenChange,
  admin,
  projects,
  onShowShortcuts,
  onSignOut,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  admin: boolean
  projects: readonly Project[]
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const [query, setQuery] = useState("")
  const popup = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { toggleSidebar } = useSidebar()
  const { scheme, setScheme } = useColourScheme()
  const onProject = useMatch("/projects/:key/*")?.params.key
  const current = projects.find(({ key }) => key === onProject)
  // C makes a new ticket on the Board alone, so the entry wears its key only there.
  const onBoard = useMatch("/projects/:key") !== null

  // ⌘K again, from inside the palette, puts it away.
  useShortcut("command-palette", () => close(), { within: popup, enabled: open })

  function close() {
    onOpenChange(false)
    setQuery("")
  }

  const jump = ticketJumpOf(query)
  const entries: Entry[] = [
    ...(jump
      ? [
          {
            id: "jump",
            group: "Jump to ticket",
            label: `Open ${jump.key}`,
            glyph: icon(ArrowRightIcon),
            run: () => navigate(jump.path),
          } as const,
        ]
      : []),
    ...[...destinations, ...(admin ? adminDestinations : [])].map(
      ({ to, title, Icon, chord }): Entry => ({
        id: `go ${to}`,
        group: "Go to",
        label: title,
        glyph: icon(Icon),
        shortcut: chord,
        run: () => navigate(to),
      }),
    ),
    ...projects.map(({ key, name }): Entry => ({
      id: `project ${key}`,
      group: "Projects",
      label: name,
      secondary: key,
      glyph: <IdentityTile projectKey={key} size={16} />,
      run: () => navigate(`/projects/${key}`),
    })),
    // A new ticket is made on its Board, so the palette takes the Member there with the composer
    // open; only where they may make one.
    ...(current && current.access === "write" && !current.archived
      ? [
          {
            id: "new ticket",
            group: "Actions",
            label: `New ticket in ${current.name}`,
            glyph: icon(PlusIcon),
            ...(onBoard && { shortcut: "new-ticket" }),
            run: () => navigate(`/projects/${current.key}?new=1`),
          } satisfies Entry,
        ]
      : []),
    {
      id: "new project",
      group: "Actions",
      label: "New Project",
      glyph: icon(PlusIcon),
      run: () => navigate("/projects?new=1"),
    },
    ...(admin
      ? [
          {
            id: "new group",
            group: "Actions",
            label: "New group",
            glyph: icon(PlusIcon),
            run: () => navigate("/groups?new=1"),
          } as const,
        ]
      : []),
    ...colourSchemes.map(({ value, label, Icon }): Entry => ({
      id: `theme ${value}`,
      group: "Actions",
      label: `Theme: ${label}`,
      ...(value === scheme && { secondary: "Current" }),
      glyph: icon(Icon),
      run: () => setScheme(value),
    })),
    {
      id: "toggle sidebar",
      group: "Actions",
      label: "Toggle sidebar",
      glyph: icon(PanelLeftIcon),
      shortcut: "toggle-sidebar",
      run: toggleSidebar,
    },
    {
      id: "copy link",
      group: "Actions",
      label: "Copy link",
      glyph: icon(CopyIcon),
      run: () => void copyText(window.location.href, "Link copied"),
    },
    {
      id: "shortcuts",
      group: "Actions",
      label: "Keyboard shortcuts",
      glyph: icon(KeyboardIcon),
      shortcut: "shortcuts",
      run: onShowShortcuts,
    },
    {
      id: "sign out",
      group: "Actions",
      label: "Sign out",
      glyph: icon(LogOutIcon),
      run: onSignOut,
    },
  ]
  // A typed key is exactly one ticket, so its jump stands alone at the top, whatever else the
  // words happen to match. They are listed group by group, the order the rows render in, so the
  // Combobox's first item is the first row it highlights.
  const shown = groups.flatMap((group) =>
    entries.filter(
      (entry) =>
        entry.group === group &&
        (group === "Jump to ticket" || answers(query, [entry.label, entry.secondary])),
    ),
  )

  function choose(id: string | null) {
    const entry = shown.find((candidate) => candidate.id === id)
    if (!entry) return
    close()
    entry.run()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent ref={popup} position="top" size="lg" showCloseButton={false}>
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Combobox
          inline
          open
          autoHighlight={highlightFirst}
          items={shown.map(({ id }) => id)}
          filter={null}
          value={null}
          onValueChange={choose}
          inputValue={query}
          onInputValueChange={(next, { reason }) => {
            if (reason === "input-change") setQuery(next)
            else if (reason === "input-clear") setQuery("")
          }}
        >
          <ComboboxInput
            variant="search"
            placeholder="Search or jump to…"
            aria-label="Search or jump to"
            // The palette's input is its title: 15px on a 48px row.
            className="h-12 px-2 [&_input]:text-title"
          />
          <ComboboxList className="max-h-96 outline-none data-empty:p-1">
            {groups.map((group) => {
              const inGroup = shown.filter((entry) => entry.group === group)
              if (inGroup.length === 0) return null
              return (
                <ComboboxGroup key={group}>
                  <ComboboxLabel>{group}</ComboboxLabel>
                  {inGroup.map((entry) => (
                    <ComboboxItem key={entry.id} value={entry.id} className="h-9 gap-2.5 px-3">
                      {entry.glyph}
                      <span className="truncate">{entry.label}</span>
                      {entry.secondary && (
                        <span className="shrink-0 text-xs text-subtle-foreground">
                          {entry.secondary}
                        </span>
                      )}
                      {entry.shortcut && (
                        <span className="ml-auto">
                          <Kbd shortcut={entry.shortcut} />
                        </span>
                      )}
                    </ComboboxItem>
                  ))}
                </ComboboxGroup>
              )
            })}
          </ComboboxList>
          {/*
           * A status region, so a screen reader hears when a query leaves nothing. It stays
           * mounted to be heard, and takes no room until it has something to say.
           */}
          <ComboboxEmpty className="flex empty:p-0" />
        </Combobox>
        <div className="flex h-8 items-center border-t border-border-subtle px-3 text-xs text-subtle-foreground">
          ↑↓ to navigate · ↵ to open · esc to close
        </div>
      </DialogContent>
    </Dialog>
  )
}
